import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  fetchFinalInvoiceContext,
  generateFinalInvoice,
  type FinalInvoiceContextResponse,
  type ProjectFinalInvoiceDetailLevel,
  type ProjectFinalInvoiceSummary,
} from "@/lib/projects-clients";
import { formatCurrency } from "@/lib/tickets";
import { useToast } from "@/hooks/use-toast";

type GenerateFinalInvoiceDialogProps = {
  projectId: string | null;
  projectName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerated?: (invoice: ProjectFinalInvoiceSummary) => void;
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function balanceLabel(value: number) {
  if (value < 0) return `Overpaid: ${formatCurrency(Math.abs(value))}`;
  return `Balance due: ${formatCurrency(value)}`;
}

export function GenerateFinalInvoiceDialog({
  projectId,
  projectName,
  open,
  onOpenChange,
  onGenerated,
}: GenerateFinalInvoiceDialogProps) {
  const { toast } = useToast();
  const [context, setContext] = useState<FinalInvoiceContextResponse | null>(
    null,
  );
  const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);
  const [detailLevel, setDetailLevel] =
    useState<ProjectFinalInvoiceDetailLevel>("SUMMARY_ONLY");
  const [showPayments, setShowPayments] = useState(true);
  const [invoiceNote, setInvoiceNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !projectId) return;

    let canceled = false;
    setLoading(true);
    setError(null);
    setContext(null);
    setDetailLevel("SUMMARY_ONLY");
    setShowPayments(true);
    setInvoiceNote("");

    fetchFinalInvoiceContext(projectId)
      .then((data) => {
        if (canceled) return;
        setContext(data);
        setSelectedTicketIds(data.tickets.map((ticket) => ticket.id));
      })
      .catch((err) => {
        if (canceled) return;
        setError(
          err instanceof Error
            ? err.message
            : "Could not load final invoice details.",
        );
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });

    return () => {
      canceled = true;
    };
  }, [open, projectId]);

  const selectedApprovedTotal = useMemo(() => {
    const tickets = context?.tickets ?? [];
    return tickets.reduce((sum, ticket) => {
      return selectedTicketIds.includes(ticket.id)
        ? sum + Number(ticket.amount ?? 0)
        : sum;
    }, 0);
  }, [context?.tickets, selectedTicketIds]);

  const paymentsReceived = Number(context?.summary.paymentsReceived ?? 0);
  const balanceDue = selectedApprovedTotal - paymentsReceived;
  const hasApprovedTickets = Number(context?.tickets.length ?? 0) > 0;
  const canGenerate = Boolean(
    projectId && selectedTicketIds.length > 0 && hasApprovedTickets,
  );

  const toggleTicket = (ticketId: string, checked: boolean) => {
    setSelectedTicketIds((current) =>
      checked
        ? Array.from(new Set([...current, ticketId]))
        : current.filter((id) => id !== ticketId),
    );
  };

  const handleGenerate = async () => {
    if (!projectId || !canGenerate) return;
    setGenerating(true);
    try {
      const invoice = await generateFinalInvoice(projectId, {
        ticketIds: selectedTicketIds,
        detailLevel,
        showPayments,
        invoiceNote: invoiceNote.trim() || undefined,
      });
      toast({
        title: "Final invoice generated",
        description: `${invoice.invoiceNumber} is ready to view or download.`,
      });
      onGenerated?.(invoice);
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Could not generate final invoice",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => !generating && onOpenChange(nextOpen)}
    >
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-[#0C1F4F]">
            Generate final invoice
          </DialogTitle>
          <DialogDescription>
            Create a final invoice from approved tickets and recorded payments
            for {context?.project.name ?? projectName ?? "this project"}.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading approved work...
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        ) : (
          <div className="space-y-5">
            <section className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-[#0C1F4F]">
                  Approved work to include
                </h3>
                <p className="text-xs text-muted-foreground">
                  Only approved tickets can be included.
                </p>
              </div>
              {!hasApprovedTickets ? (
                <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
                  Final invoice cannot be generated until at least one ticket is
                  approved.
                </div>
              ) : (
                <div className="divide-y rounded-lg border">
                  {context?.tickets.map((ticket) => (
                    <label
                      key={ticket.id}
                      className="flex cursor-pointer items-start gap-3 px-4 py-3 text-sm hover:bg-muted/20"
                    >
                      <Checkbox
                        checked={selectedTicketIds.includes(ticket.id)}
                        onCheckedChange={(checked) =>
                          toggleTicket(ticket.id, checked === true)
                        }
                        className="mt-0.5"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1 font-medium text-[#0C1F4F]">
                          <span>{ticket.ticketNumber}</span>
                          <span className="text-muted-foreground">·</span>
                          <span>{ticket.typeLabel}</span>
                          <span className="text-muted-foreground">·</span>
                          <span>
                            {formatDate(ticket.approvedAt ?? ticket.createdAt)}
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-muted-foreground">
                          {ticket.title || "Untitled ticket"}
                        </span>
                      </span>
                      <span className="shrink-0 font-semibold text-accent">
                        {formatCurrency(Number(ticket.amount ?? 0))}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {hasApprovedTickets && selectedTicketIds.length === 0 ? (
                <p className="text-xs font-medium text-destructive">
                  Select at least one approved ticket.
                </p>
              ) : null}
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-[#0C1F4F]">
                Invoice detail level
              </h3>
              <RadioGroup
                value={detailLevel}
                onValueChange={(value) =>
                  setDetailLevel(value as ProjectFinalInvoiceDetailLevel)
                }
                className="grid gap-3 sm:grid-cols-2"
              >
                <label className="flex cursor-pointer gap-3 rounded-lg border p-3 hover:bg-muted/20">
                  <RadioGroupItem value="SUMMARY_ONLY" className="mt-0.5" />
                  <span>
                    <span className="block text-sm font-medium text-[#0C1F4F]">
                      Summary only
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Show approved tickets and totals only.
                    </span>
                  </span>
                </label>
                <label className="flex cursor-pointer gap-3 rounded-lg border p-3 hover:bg-muted/20">
                  <RadioGroupItem value="WITH_BREAKDOWNS" className="mt-0.5" />
                  <span>
                    <span className="block text-sm font-medium text-[#0C1F4F]">
                      Include ticket breakdowns where available
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Use customer-safe breakdown details from selected tickets.
                    </span>
                  </span>
                </label>
              </RadioGroup>
            </section>

            <section className="space-y-3">
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-muted/20">
                <Checkbox
                  checked={showPayments}
                  onCheckedChange={(checked) =>
                    setShowPayments(checked === true)
                  }
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm font-medium text-[#0C1F4F]">
                    Show payments on final invoice
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Include recorded payments and balance details on the
                    generated invoice.
                  </span>
                </span>
              </label>
            </section>

            <section
              className={`grid gap-3 rounded-lg border bg-muted/20 p-4 text-sm ${
                showPayments ? "sm:grid-cols-3" : "sm:grid-cols-1"
              }`}
            >
              <p>
                <span className="block text-xs text-muted-foreground">
                  Selected approved total
                </span>
                <span className="font-semibold text-[#0C1F4F]">
                  {formatCurrency(selectedApprovedTotal)}
                </span>
              </p>
              {showPayments ? (
                <>
                  <p>
                    <span className="block text-xs text-muted-foreground">
                      Payments received
                    </span>
                    <span className="font-semibold text-[#0C1F4F]">
                      {formatCurrency(paymentsReceived)}
                    </span>
                  </p>
                  <p>
                    <span className="block text-xs text-muted-foreground">
                      {balanceDue < 0 ? "Overpaid" : "Balance due"}
                    </span>
                    <span className="font-semibold text-accent">
                      {balanceLabel(balanceDue).replace(
                        /^Balance due: |^Overpaid: /,
                        "",
                      )}
                    </span>
                  </p>
                </>
              ) : null}
            </section>

            <div className="space-y-1.5">
              <Label htmlFor="final-invoice-note">Invoice note</Label>
              <Textarea
                id="final-invoice-note"
                value={invoiceNote}
                onChange={(event) => setInvoiceNote(event.target.value)}
                placeholder="Thank you for your business. Please review the final balance due."
                rows={3}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={generating}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleGenerate()}
            disabled={!canGenerate || generating || loading}
            className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {generating ? "Generating..." : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
